import { Controller } from "@hotwired/stimulus";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { verifyLocally, loadBundle, localScanState, markScannedLocally } from "../lib/offline_store";

// Drives the camera scanner: decodes Aztec codes, posts them to the scan
// endpoint, plays a beep, and shows a result card for 5s before resuming.
export default class extends Controller {
  static targets = ["video", "result", "resultTemplate", "rowTemplate", "labels", "expected", "scanned", "open", "search", "list", "manual", "torch", "mode"];
  static values = { scanUrl: String, ticketsUrl: String, ticketTypeIds: Array, performanceId: String };

  connect() {
    this.inFlight = false;
    this.showing = false;
    this.offline = false;
    this.armAudioUnlock();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.AZTEC]);
    this.reader = new BrowserMultiFormatReader(hints);
    // @zxing/browser@0.2.0: decodeFromConstraints returns a Promise<IScannerControls>.
    // Store the controls object for teardown via controls.stop().
    // facingMode "environment" = rear camera (what decodeFromVideoDevice(undefined)
    // defaults to); the frameRate cap trades scan latency we don't need (Aztec
    // codes are static) for less camera/CPU power — battery saving at the door.
    const constraints = { video: { facingMode: "environment", frameRate: { ideal: 10, max: 15 } } };
    this.reader
      .decodeFromConstraints(constraints, this.videoTarget, (result) => {
        // The camera decodes continuously. Ignore frames while a scan is in
        // flight OR while a result is still on screen — the displayed result is
        // the camera's cooldown, so the same code isn't re-fired many times/sec.
        if (result && !this.inFlight && !this.showing) this.submit(result.getText());
      })
      .then((controls) => {
        this.controls = controls;
        this.revealTorchIfSupported();
      })
      .catch(() => {});
  }

  // The torch (camera light) is a constraint on the video MediaStreamTrack, not
  // a ZXing feature. Only Android Chrome and a few others expose `torch` in the
  // track capabilities — iOS and desktop don't — so the button stays hidden
  // unless the running track actually reports support.
  revealTorchIfSupported() {
    this.torchOn = false;
    const track = this.videoTarget.srcObject?.getVideoTracks?.()[0];
    if (track?.getCapabilities?.().torch) this.torchTarget.hidden = false;
  }

  async toggleTorch() {
    const track = this.videoTarget.srcObject?.getVideoTracks?.()[0];
    if (!track) return;
    this.torchOn = !this.torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: this.torchOn }] });
      this.torchTarget.classList.toggle("scan-torch--on", this.torchOn);
    } catch {
      // Some drivers reject applyConstraints even when capabilities claim torch
      // support; revert so the button doesn't claim the light is on.
      this.torchOn = !this.torchOn;
    }
  }

  disconnect() {
    // @zxing/browser@0.2.0 uses controls.stop() to release the camera stream.
    // reader.reset() does not exist in this version; the optional chain is a safe no-op.
    // Stopping the track also turns the torch off — no explicit torch teardown needed.
    clearTimeout(this.clearTimer);
    clearTimeout(this.searchTimer);
    this.controls?.stop?.();
    this.reader?.reset?.();
    document.removeEventListener("touchend", this.unlockAudio);
    document.removeEventListener("mousedown", this.unlockAudio);
    this.audio?.close?.();
  }

  // iOS Safari starts every AudioContext suspended and only resumes it inside a
  // user gesture. The beep fires from the decode callback (never a gesture), so
  // unlock a single shared context on the first tap and reuse it for every beep.
  armAudioUnlock() {
    this.unlockAudio = () => {
      const ctx = this.audioContext();
      if (ctx.state === "suspended") ctx.resume();
    };
    document.addEventListener("touchend", this.unlockAudio, { once: true });
    document.addEventListener("mousedown", this.unlockAudio, { once: true });
  }

  audioContext() {
    this.audio ||= new (window.AudioContext || window.webkitAudioContext)();
    return this.audio;
  }

  manualSubmit(event) {
    event.preventDefault();
    const code = this.manualTarget.value.trim();
    this.manualTarget.value = "";
    // Manual / hardware-scanner entry is human-paced and explicit, so it always
    // submits and preempts whatever result is on screen — it is NOT subject to
    // the camera's 5s result-display cooldown. submit() still guards re-entrancy
    // so two entries can't overlap mid-request.
    if (code) this.submit(code);
  }

  async submit(code) {
    if (this.inFlight) return; // ignore re-entrant calls while a scan is mid-flight
    this.inFlight = true;
    clearTimeout(this.clearTimer); // preempt any result still being displayed
    try {
      const data = this.offline ? this.submitOffline(code) : await this.submitOnline(code);
      this.render(data);
      this.showing = true;
      this.clearTimer = setTimeout(() => this.clear(), 5000);
    } catch {
      // Don't leave a result stuck on a failed scan (storage full, network down).
      this.clear();
    } finally {
      this.inFlight = false;
    }
  }

  async submitOnline(code) {
    const response = await fetch(this.scanUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify({ code, ticket_type_ids: this.ticketTypeIdsValue }),
    });
    const data = await response.json();
    // Keep the offline cache in step with online scans so the two modes can't
    // double-admit the same ticket:
    //   • If we already scanned this offline (queued, not yet synced), the
    //     server still reports it "valid" — override to already_scanned so we
    //     don't admit it a second time.
    //   • On any accepted scan, write st="scanned" into the local bundle (no
    //     queue push — the server already has it) so a later offline scan of the
    //     same code returns already_scanned.
    if (data.outcome === "valid" || data.outcome === "already_scanned") {
      if (data.outcome === "valid" && localScanState(this.performanceIdValue, code) === "scanned") {
        data.outcome = "already_scanned";
      }
      markScannedLocally(this.performanceIdValue, code);
    }
    return data;
  }

  submitOffline(code) {
    const ids = this.ticketTypeIdsValue.map(Number);
    const result = verifyLocally(this.performanceIdValue, code, ids);
    const t = result.ticket;
    return {
      outcome: result.outcome,
      ticket: t && { name: t.name, ticket_type: t.ticket_type, persons: t.persons, scanned_at: null },
      counts: null, // offline: counts aren't authoritative; refreshed at sync
    };
  }

  toggleMode() {
    this.offline = !this.offline;
    this.modeTarget.textContent = this.offline ? "Offline-Modus" : "Online";
    this.modeTarget.classList.toggle("scan-mode--offline", this.offline);
  }

  render(data) {
    const valid = data.outcome === "valid";
    this.beep(valid ? 880 : 220, valid ? 150 : 400);

    const t = data.ticket;
    const card = this.resultTemplateTarget.content.cloneNode(true);
    this.slot(card, "title").textContent = this.label(data.outcome);

    if (t) {
      this.slot(card, "name").textContent = t.name;
      this.slot(card, "meta").textContent = `${t.ticket_type} · ${t.persons}P`;
    } else {
      this.slot(card, "name").remove();
      this.slot(card, "meta").remove();
    }

    if (data.outcome === "already_scanned" && t && t.scanned_at) {
      this.slot(card, "scannedAt").textContent = t.scanned_at;
    } else {
      this.slot(card, "scannedAt").remove();
    }

    this.resultTarget.className = `scan-result scan-result--${data.outcome}`;
    this.resultTarget.replaceChildren(card);

    if (data.counts) {
      this.expectedTarget.textContent = data.counts.expected;
      this.scannedTarget.textContent = data.counts.scanned;
      this.openTarget.textContent = data.counts.open;
    }
  }

  clear() {
    this.resultTarget.className = "scan-result";
    this.resultTarget.replaceChildren();
    this.showing = false;
  }

  // Tapping a row in the search list scans that ticket (same path as manual
  // entry), then clears the list and the query for the next lookup. submit()
  // handles online/offline, the beep, the result card and re-entrancy.
  scanRow(event) {
    const code = event.currentTarget.dataset.scanCode;
    if (code) this.submit(code);
    this.listTarget.replaceChildren();
    this.searchTarget.value = "";
  }

  // Debounced: a fast typist shouldn't fire a request (or an offline scan of the
  // whole bundle) on every keystroke. 200ms after the last input we run once.
  search() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runSearch(), 200);
  }

  async runSearch() {
    try {
      const rows = this.offline ? this.searchOffline() : await this.searchOnline();
      this.renderRows(rows);
    } catch {
      // Network error while searching online — leave the current list as-is
      // rather than throwing an unhandled rejection.
    }
  }

  async searchOnline() {
    const url = new URL(this.ticketsUrlValue, window.location.origin);
    this.ticketTypeIdsValue.forEach((id) => url.searchParams.append("ticket_type_ids[]", id));
    if (this.searchTarget.value.trim()) url.searchParams.set("q", this.searchTarget.value.trim());
    return (await fetch(url)).json();
  }

  // Mirrors tickets#index: scope to the selected types, filter by code or name
  // substring (case-insensitive), cap at 200. Reads the cached bundle directly.
  searchOffline() {
    const bundle = loadBundle(this.performanceIdValue);
    if (!bundle) return [];
    const ids = this.ticketTypeIdsValue.map(Number);
    const q = this.searchTarget.value.trim().toLowerCase();

    return bundle.tickets
      .filter((t) => ids.includes(t.tt))
      .filter((t) => !q || t.code.toLowerCase().includes(q) || (t.n || "").toLowerCase().includes(q))
      .slice(0, 200)
      .map((t) => ({ code: t.code, name: t.n, ticket_type: t.tn, state: t.st }));
  }

  renderRows(rows) {
    this.listTarget.replaceChildren(
      ...rows.map((r) => {
        const row = this.rowTemplateTarget.content.cloneNode(true);
        const li = row.querySelector(".scan-list__row");
        li.classList.add(`scan-list__row--${r.state}`);
        li.dataset.scanCode = r.code;
        this.slot(row, "code").textContent = r.code;
        this.slot(row, "name").textContent = r.name;
        this.slot(row, "ticketType").textContent = r.ticket_type;
        return row;
      }),
    );
  }

  // Looks up the German outcome label from the <template> in the view, so all
  // user-facing copy stays in HAML rather than in this controller.
  label(outcome) {
    const el = this.labelsTarget.content.querySelector(`[data-outcome="${outcome}"]`);
    return el ? el.textContent : outcome;
  }

  slot(root, name) {
    return root.querySelector(`[data-scan-slot="${name}"]`);
  }

  beep(frequency, duration) {
    const ctx = this.audioContext();
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    osc.frequency.value = frequency;
    osc.connect(ctx.destination);
    osc.start();
    setTimeout(() => osc.stop(), duration);
  }
}
